import React, { useState, useEffect } from 'react';
import store, { loanActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminError, handleAdminSuccess, handleAdminWarning } from '../../common/SharedFunctions';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { NumericFormat } from 'react-number-format';
import { UnderwritingPaidTypeOptions } from '../common/constants/UnderwritingPaidTypeOptions';
import { ILoanSetupSheetOwners } from '@apply/api/src/api/loanApi';
import FileDownload from 'js-file-download';
import { LoanType } from '@apply/constants';

const LoanSetupSheetModal = () => {
    const { isOpen, loanId, bfiNumber, loanTypeId } = useSelector((state) => state.loan.loanSetupSheetModal);
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [uwScore, set_uwScore] = useState('');
    const [uwBankruptcyPlusScore, set_uwBankruptcyPlusScore] = useState('');
    const [netWorthAmount, set_netWorthAmount] = useState('');
    const [netWorth, set_netWorth] = useState('');
    const [adjustedNetWorth, set_adjustedNetWorth] = useState('');
    const [adjustedNetWorthAmount, set_adjustedNetWorthAmount] = useState('');
    const [anyUseFloatingRate, set_anyUseFloatingRate] = useState('');
    const [floatingRateForInterestRate, set_floatingRateForInterestRate] = useState('');
    const [interestRateAtStart, set_interestRateAtStart] = useState('');
    const [hasRateCeiling, set_hasRateCeiling] = useState('');
    const [floatingRateForCeiling, set_floatingRateForCeiling] = useState('');
    const [interestRateCeiling, set_interestRateCeiling] = useState('');
    const [hasRateFloor, set_hasRateFloor] = useState('');
    const [floatingRateForFloor, set_floatingRateForFloor] = useState('');
    const [interestRateFloor, set_interestRateFloor] = useState('');
    const [underwritingFeePaid, set_underwritingFeePaid] = useState('');
    const [estimatedInterestAndFees, set_estimatedInterestAndFees] = useState('');
    const [estimatedClosingCosts, set_estimatedClosingCosts] = useState('');
    //Credit scores array
    const [ownerCreditScores, set_ownerCreditScores] = useState<ILoanSetupSheetOwners[]>([]);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            marginTop: '10px',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px' },
    };

    const additionalSuppressedRateLoanTypes = [LoanType.MasterNote, LoanType.SubNoteConstruction, LoanType.SubNoteFixFlip];

    useEffect(() => {
        async function loadInformation() {
            try {
                const response = await loanApi.getApplicableLoanSetupSheetOwners(loanId);

                if (response.statusCode !== 200) {
                    set_loadingOpen(false);
                    return;
                }

                if (response?.result) {
                    const applicableOwners = response.result.map((o) => ({
                        principalId: o.principalId,
                        entityName: o.entityName,
                        name: o.name,
                        percentOwnership: o.percentOwnership,
                        creditScore: '', // user will fill this in
                    }));

                    set_ownerCreditScores(applicableOwners);
                }

                //Master Notes and Subnotes should suppress Rate Ceiling and Floor by default - per reqs
                if (additionalSuppressedRateLoanTypes.includes(loanTypeId)) {
                    set_hasRateCeiling('suppress');
                    set_hasRateFloor('suppress');
                }

                set_loadingOpen(false);
            } catch (ex) {
                set_loadingOpen(false);
                handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
            }
        }

        if (isOpen) {
            loadInformation();
        }
    }, [isOpen]);

    const updateCreditScore = (principalId, score) => {
        set_ownerCreditScores((prev) => prev.map((o) => (o.principalId === principalId ? { ...o, creditScore: score } : o)));
    };

    const resetState = () => {
        set_uwScore('');
        set_uwBankruptcyPlusScore('');
        set_netWorthAmount('');
        set_netWorth('');
        set_adjustedNetWorth('');
        set_adjustedNetWorthAmount('');
        set_anyUseFloatingRate('');
        set_floatingRateForInterestRate('');
        set_interestRateAtStart('');
        set_hasRateCeiling('');
        set_floatingRateForCeiling('');
        set_interestRateCeiling('');
        set_hasRateFloor('');
        set_floatingRateForFloor('');
        set_interestRateFloor('');
        set_underwritingFeePaid('');
        set_estimatedInterestAndFees('');
        set_estimatedClosingCosts('');
        set_ownerCreditScores([]);
    };

    const validateFields = () => {
        const required: Array<{ name: string; value: any }> = [
            { name: 'uwScore', value: uwScore },
            { name: 'uwBankruptcyPlusScore', value: uwBankruptcyPlusScore },
            { name: 'anyUseFloatingRate', value: anyUseFloatingRate },
            { name: 'floatingRateForInterestRate', value: floatingRateForInterestRate },
            { name: 'interestRateAtStart', value: interestRateAtStart },
            { name: 'underwritingFeePaid', value: underwritingFeePaid },
            { name: 'estimatedInterestAndFees', value: estimatedInterestAndFees },
            { name: 'estimatedClosingCosts', value: estimatedClosingCosts },
        ];

        if (hasRateCeiling === 'yes') {
            required.push(
                { name: 'floatingRateForCeiling', value: floatingRateForCeiling },
                { name: 'interestRateCeiling', value: interestRateCeiling }
            );
        }

        if (hasRateFloor === 'yes') {
            required.push(
                { name: 'floatingRateForFloor', value: floatingRateForFloor },
                { name: 'interestRateFloor', value: interestRateFloor }
            );
        }

        if (netWorth === 'provide') {
            required.push({ name: 'netWorthAmount', value: netWorthAmount });
        }

        if (adjustedNetWorth === 'provide') {
            required.push({ name: 'adjustedNetWorthAmount', value: adjustedNetWorthAmount });
        }

        ownerCreditScores.forEach((o, i) =>
            required.push({
                name: `ownerCreditScores[${i}].creditScore`,
                value: o.creditScore,
            })
        );

        for (const field of required) {
            if (field.value == null || String(field.value).trim() === '') {
                console.log('Invalid field:', field.name);
                return false;
            }
        }

        return true;
    };

    const handleClose = () => {
        resetState();

        dispatch(
            loanActions.setLoanSetupSheetModal({
                loanSetupSheetModal: { isOpen: false },
            })
        );
    };

    const convertToNumber = (value: string) => {
        return Number(String(value).trim());
    };

    const convertToBool = (value: string) => {
        return String(value).trim().toLowerCase() === 'yes';
    };

    const convertSuppressToBool = (value: string) => {
        return String(value).trim().toLowerCase() === 'suppress';
    };

    const generateSetupSheet = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        //Validation
        if (!validateFields()) {
            handleAdminWarning('Please fill out all required fields.');
            return;
        }

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                //Set Model
                uwScore: convertToNumber(uwScore),
                uwBankruptcyPlusScore: convertToNumber(uwBankruptcyPlusScore),
                floatingRateForInterestRate: convertToBool(floatingRateForInterestRate),
                interestRateAtStart: convertToNumber(interestRateAtStart),
                suppressCeiling: convertSuppressToBool(hasRateCeiling),
                hasCeiling: convertToBool(hasRateCeiling),
                floatingRateForCeiling: convertToBool(floatingRateForCeiling),
                interestRateCeiling: convertToNumber(interestRateCeiling),
                suppressFloor: convertSuppressToBool(hasRateFloor),
                hasFloor: convertToBool(hasRateFloor),
                floatingRateForFloor: convertToBool(floatingRateForFloor),
                interestRateFloor: convertToNumber(interestRateFloor),
                netWorthAmount: String(netWorthAmount).trim() === '' ? undefined : convertToNumber(netWorthAmount),
                adjustedNetWorthAmount:
                    String(adjustedNetWorthAmount).trim() == '' ? undefined : convertToNumber(adjustedNetWorthAmount),
                underwritingFeePaid: convertToNumber(underwritingFeePaid),
                estimatedInterestAndFees: convertToNumber(estimatedInterestAndFees),
                estimatedClosingCosts: convertToNumber(estimatedClosingCosts),
                owners: ownerCreditScores,
            };

            await loanApi.generateLoanSetupSheet(loanId, model).then((response: any) => {
                const fileName = `loan${bfiNumber ? '-' + bfiNumber : ''}-setup.xlsx`;

                FileDownload(response.data, fileName);

                handleAdminSuccess('Loan Setup Sheet Generated.');
                set_loadingOpen(false);
            });

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '900px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>Loan Setup Sheet</h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={handleClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                        </div>
                        <div
                            className='main-content no-search'
                            style={{ padding: '0', width: '100%' }}
                        >
                            <form
                                name='generateLoanSetupSheetForm'
                                id='generateLoanSetupSheetForm'
                                onSubmit={(event) => generateSetupSheet(event)}
                                className={'standard-form'}
                            >
                                <div className='scrollable-content'>
                                    <div style={{ paddingBottom: '10px' }}>
                                        <strong>BFI Number:</strong>
                                        <span style={{ paddingLeft: '10px' }}>{bfiNumber ? bfiNumber : 'Not Provided'}</span>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        UW Scores
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='uwScore'>
                                                UW Score
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='uwScore'
                                                type='text'
                                                value={uwScore}
                                                onChange={(event) => {
                                                    const val = event.target.value;

                                                    // Allow only digits, max 3
                                                    if (/^\d{0,3}$/.test(val)) {
                                                        set_uwScore(val);
                                                    }
                                                }}
                                                maxLength={3}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>3 Character Credit Score</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-4 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='uwBankruptcyPlusScore'>
                                                UW Bankruptcy Plus Score
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='uwBankruptcyPlusScore'
                                                type='text'
                                                value={uwBankruptcyPlusScore}
                                                onChange={(event) => {
                                                    const val = event.target.value;

                                                    // Only allow 0–3 digits
                                                    if (/^\d{0,3}$/.test(val)) {
                                                        set_uwBankruptcyPlusScore(val);
                                                    }
                                                }}
                                                maxLength={3}
                                                required={true}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>3 Character Bankruptcy Plus Score</span>
                                            </span>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Owner Credit Scores
                                    </h2>
                                    <div
                                        className='row'
                                        style={{ paddingLeft: '15px', paddingRight: '15px' }}
                                    >
                                        {ownerCreditScores.length === 0 && <div>No Owners with 20% or more Ownership found.</div>}

                                        {ownerCreditScores.length > 0 && (
                                            <div style={{ width: '100%' }}>
                                                <div>Owners found with 20% or more Ownership:</div>

                                                <table
                                                    className='table table-bordered'
                                                    style={{ width: '100%' }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '40%' }}>Entity Name</th>
                                                            <th style={{ width: '40%' }}>Owner Name</th>
                                                            <th style={{ width: '120px' }}>Credit Score</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {ownerCreditScores.map((owner) => (
                                                            <tr key={owner.principalId}>
                                                                <td>{owner.entityName}</td>
                                                                <td>{owner.name}</td>

                                                                <td>
                                                                    <input
                                                                        id={`creditScore-${owner.principalId}`}
                                                                        type='text'
                                                                        value={owner.creditScore}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;

                                                                            // Only allow 0–3 digits
                                                                            if (/^\d{0,3}$/.test(val)) {
                                                                                updateCreditScore(owner.principalId, val);
                                                                            }
                                                                        }}
                                                                        maxLength={3}
                                                                        required
                                                                        style={{
                                                                            width: '200px',
                                                                            textAlign: 'left',
                                                                        }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Net Worth
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='Net Worth'>
                                                Net Worth
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='netWorth'
                                                value={netWorth}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_netWorth(value);

                                                    // If user selects anything other than "provide", clear the amount
                                                    if (value !== 'provide') {
                                                        set_netWorthAmount('');
                                                    }
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>
                                                <option value='provide'>Provide Amount</option>
                                                <option value='n/a'>N/A</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Is Net Worth Applicable?</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='netWorthAmount'>
                                                Net Worth Amount<span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                thousandSeparator=','
                                                prefix='$'
                                                id='netWorthAmount'
                                                name='netWorthAmount'
                                                placeholder='$'
                                                value={netWorthAmount}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                disabled={netWorth != 'provide'}
                                                onValueChange={(values) => {
                                                    set_netWorthAmount(values.floatValue);
                                                }}
                                                required={true}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value > 0;
                                                }}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Enter the net worth.</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='Net Worth'>
                                                Adjusted Net Worth
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='adjustedNetWorthAmount'
                                                value={adjustedNetWorth}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_adjustedNetWorth(value);

                                                    // If user selects anything other than "provide", clear the amount
                                                    if (value !== 'provide') {
                                                        set_adjustedNetWorthAmount('');
                                                    }
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>

                                                <option value='provide'>Provide Amount</option>
                                                <option value='n/a'>N/A</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Is Adjusted Net Worth applicable?</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='netWorthAmount'>
                                                Adjust. Net Worth Amt.<span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                thousandSeparator=','
                                                prefix='$'
                                                id='adjustedNetWorthAmount'
                                                name='adjustedNetWorthAmount'
                                                placeholder='$'
                                                value={adjustedNetWorthAmount}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                onValueChange={(values) => {
                                                    set_adjustedNetWorthAmount(values.floatValue);
                                                }}
                                                required={true}
                                                disabled={adjustedNetWorth != 'provide'}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value > 0;
                                                }}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Enter the Adjusted Net Worth.</span>
                                            </span>
                                        </div>
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Finance Terms
                                    </h2>
                                    <div
                                        className='row'
                                        style={{ paddingBottom: '10px' }}
                                    >
                                        <div
                                            className='columns small-12 medium-4 large-5'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='anyUseFloatingRate'>
                                                Floating Rate applicable such as Prime+ ?<span className='req'>*</span>
                                            </label>
                                            <select
                                                id='anyUseFloatingRate'
                                                value={anyUseFloatingRate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_anyUseFloatingRate(value);
                                                    // If user selects anything other than "yes", set linked values to false
                                                    if (value == 'no') {
                                                        set_floatingRateForInterestRate('no');
                                                        set_floatingRateForCeiling('no');
                                                        set_floatingRateForFloor('no');
                                                    }
                                                    if (value !== 'no') {
                                                        set_floatingRateForInterestRate('');
                                                        set_floatingRateForCeiling('');
                                                        set_floatingRateForFloor('');
                                                    }
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>

                                                <option value='yes'>Yes</option>
                                                <option value='no'>No</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Do any of these use a floating rate such as Prime+ ?</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='floatingRateForInterestRate'>
                                                Use Floating Rate?<span className='req'>*</span>
                                            </label>
                                            <select
                                                id='floatingRateForInterestRate'
                                                value={floatingRateForInterestRate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_floatingRateForInterestRate(value);
                                                }}
                                                required
                                                disabled={anyUseFloatingRate == 'no'}
                                            >
                                                <option value=''>Select</option>
                                                <option value='yes'>Yes</option>
                                                <option value='no'>No</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>
                                                    Use Floating Rate (such as Prime+) for interest rate at start?
                                                </span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='interestRateAtStart'>
                                                {floatingRateForInterestRate == 'yes'
                                                    ? 'Spread Over Prime at Start'
                                                    : 'Interest Rate at Start'}
                                                <span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                id='interestRateAtStart'
                                                onValueChange={(values) => {
                                                    set_interestRateAtStart(values.floatValue);
                                                }}
                                                decimalScale={2}
                                                allowNegative={false}
                                                placeholder='%'
                                                suffix={'%'}
                                                value={interestRateAtStart}
                                                displayType='input'
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value > 0;
                                                }}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>The Interest Rate at start (1FBUSA Int Rate)</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='hasRateFloor'>
                                                Has Rate Floor?<span className='req'>*</span>
                                            </label>
                                            <select
                                                id='hasRateFloor'
                                                value={hasRateFloor}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_hasRateFloor(value);
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>
                                                <option value='yes'>Yes</option>
                                                <option value='no'>No (N/A)</option>
                                                <option value='suppress'>No (Hide)</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Is Rate Floor applicable for this loan?</span>
                                            </span>
                                        </div>
                                        {hasRateFloor == 'yes' && (
                                            <>
                                                <div
                                                    className='columns small-12 medium-4 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='floatingRateForFloor'>
                                                        Use Floating Rate?<span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='floatingRateForFloor'
                                                        value={floatingRateForFloor}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            set_floatingRateForFloor(value);
                                                        }}
                                                        required
                                                        disabled={anyUseFloatingRate == 'no'}
                                                    >
                                                        <option value=''>Select</option>

                                                        <option value='yes'>Yes</option>
                                                        <option value='no'>No</option>
                                                    </select>
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>
                                                            Use Floating Rate (such as Prime+) for Floor?
                                                        </span>
                                                    </span>
                                                </div>
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='interestRateFloor'>
                                                        {floatingRateForFloor == 'yes'
                                                            ? 'Spread Over Prime Floor'
                                                            : 'Interest Rate Floor'}{' '}
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='interestRateFloor'
                                                        onValueChange={(values) => {
                                                            set_interestRateFloor(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        placeholder='%'
                                                        suffix={'%'}
                                                        value={interestRateFloor}
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                    />
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>The Interest Rate Floor</span>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-3'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='hasRateCeiling'>
                                                Has Rate Ceiling?<span className='req'>*</span>
                                            </label>
                                            <select
                                                id='hasRateCeiling'
                                                value={hasRateCeiling}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_hasRateCeiling(value);
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>
                                                <option value='yes'>Yes</option>
                                                <option value='no'>No (N/A)</option>
                                                <option value='suppress'>No (Hide)</option>
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Is Rate Ceiling applicable for this loan?</span>
                                            </span>
                                        </div>
                                        {hasRateCeiling == 'yes' && (
                                            <>
                                                <div
                                                    className='columns small-12 medium-4 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='floatingRateForCeiling'>
                                                        Use Floating Rate?<span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='floatingRateForCeiling'
                                                        value={floatingRateForCeiling}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            set_floatingRateForCeiling(value);
                                                        }}
                                                        required
                                                        disabled={anyUseFloatingRate == 'no'}
                                                    >
                                                        <option value=''>Select</option>
                                                        <option value='yes'>Yes</option>
                                                        <option value='no'>No</option>
                                                    </select>
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>
                                                            Use Floating Rate (such as Prime+) for Ceiling?
                                                        </span>
                                                    </span>
                                                </div>
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='interestRateCeiling'>
                                                        {floatingRateForCeiling == 'yes'
                                                            ? 'Spread Over Prime Ceiling'
                                                            : 'Interest Rate Ceiling'}
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='interestRateCeiling'
                                                        onValueChange={(values) => {
                                                            set_interestRateCeiling(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        placeholder='%'
                                                        suffix={'%'}
                                                        value={interestRateCeiling}
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value > 0;
                                                        }}
                                                    />
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>The Interest Rate Ceiling</span>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <h2
                                        className='decorated'
                                        style={styles.sectionHeader}
                                    >
                                        Fees and Costs
                                    </h2>
                                    <div className='row'>
                                        <div
                                            className='columns small-12 medium-4 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='Underwriting Fee Paid'>
                                                Underwriting Fee is Paid?
                                                <span className='req'>*</span>
                                            </label>
                                            <select
                                                id='underwritingFeePaid'
                                                value={underwritingFeePaid}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    set_underwritingFeePaid(value);
                                                }}
                                                required
                                            >
                                                <option value=''>Select</option>
                                                {UnderwritingPaidTypeOptions.map((fs) => {
                                                    return (
                                                        <option
                                                            key={fs.id}
                                                            value={fs.id}
                                                        >
                                                            {fs.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Has the Underwriting Fee been paid for?</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='estimatedInterestAndFees'>
                                                Estimated Interest and Fees<span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                thousandSeparator=','
                                                prefix='$'
                                                id='estimatedInterestAndFees'
                                                name='estimatedInterestAndFees'
                                                placeholder='$'
                                                value={estimatedInterestAndFees}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                onValueChange={(values) => {
                                                    set_estimatedInterestAndFees(values.floatValue);
                                                }}
                                                required={true}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value >= 0;
                                                }}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Estimated Interest and Fees of the Loan</span>
                                            </span>
                                        </div>
                                        <div
                                            className='columns small-12 large-4'
                                            style={styles.formColumn}
                                        >
                                            <label htmlFor='estimatedClosingCosts'>
                                                Estimated Closing Costs<span className='req'>*</span>
                                            </label>
                                            <NumericFormat
                                                thousandSeparator=','
                                                prefix='$'
                                                id='estimatedClosingCosts'
                                                name='estimatedClosingCosts'
                                                placeholder='$'
                                                value={estimatedClosingCosts}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                onValueChange={(values) => {
                                                    set_estimatedClosingCosts(values.floatValue);
                                                }}
                                                required={true}
                                                isAllowed={(values, sourceInfo) => {
                                                    const value = values?.floatValue;
                                                    return value === undefined || value === null || value >= 0;
                                                }}
                                            />
                                            <span className='helper'>
                                                <span className='tooltip-trigger'>
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                </span>
                                                <span className='text'>Estimated Closing Costs of the Loan</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                        title='Generate Sheet'
                                        disabled={false}
                                    >
                                        Generate Sheet
                                    </button>
                                    <button
                                        type='button'
                                        className='button hollow'
                                        data-dismiss='modal'
                                        onClick={handleClose}
                                        title='Cancel Sheet Generation'
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default LoanSetupSheetModal;
